import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import { ToastProgrammatic as Toast } from 'buefy';
import isBoolean from 'lodash/isBoolean';
import _cloneDeep from 'lodash/cloneDeep';
import useVuelidate from '@vuelidate/core';
import isNil from 'lodash/isNil';
import { V1Scheduler } from 'takeorder-groupbuying-scheduler-js';
import { helpers, maxValue, minValue } from '@vuelidate/validators';
import { cloneDeep } from 'lodash';
import {
  required,
  requiredIf,
  integer,
} from '@/utils/validators';
import {
  ALLOW_EDIT_TYPES,
  CONSUMPTION_LIMIT,
  ENTREE_LIMIT,
  ON_SITE_ORDER_ITEM_INDEX,
  ORDER_STATUS_WITH_TYPE_TEXT,
  SNACKBAR_TYPES,
} from '@/utils/constants';
import weekdayTransformer from '@/utils/weekdayTransformer';
import {
  CREATE_SCHEDULE, SET_DEPARTMENT_LIST, UPDATE_DRAFT_ORDER, UPDATE_SCHEDULE_VER2,
} from '@/store/orders/constants';
import {
  GET_MENU_ITEM_LIST,
  GET_ORDER_METHOD_MAPPING,
  GET_PAYMENT_METHOD_MAPPING,
  GET_PAYMENT_TIMING_MAPPING,
  GET_STORE_LIST,
  GET_STORE,
} from '@/store/store/constants';
import {
  GET_GROUP_BUYINGS_MENU,
} from '@/store/menu/constants';
import { GET_DEPARTMENT_LIST } from '@/store/setting/constants';
import SchedulePeriodSetting from '@/components/Order/ScheduleSetting/SchedulePeriodSetting/SchedulePeriodSetting.vue';
import ScheduleModal from '@/components/Order/ScheduleModal/ScheduleModal.vue';
import ScheduleList from '@/components/Order/OrderCreate/ScheduleList/ScheduleList.vue';
import StoreSelectorModal from '@/components/Order/ScheduleSetting/StoreSelectorModal/StoreSelectorModal.vue';
import ScheduleOrderList from '@/components/Order/ScheduleSetting/ScheduleOrderList/ScheduleOrderList.vue';
import { toSnakeCase } from '@/utils/caseConvertor';

dayjs.locale('zh-tw');

export default {
  name: 'OrderDetail',
  components: {
    ScheduleList,
    ScheduleModal,
    ScheduleOrderList,
    SchedulePeriodSetting,
    StoreSelectorModal,
  },
  props: {
    createMode: {
      default: false,
      type: Boolean,
    },
    inDialog: {
      default: false,
      type: Boolean,
    },
    groupBuyingSetting: {
      default: () => ({}),
      type: Object,
    },
    groupBuyings: {
      default: () => [],
      type: Array,
    },
    scheduleSetting: {
      default: () => ({}),
      type: Object,
    },
    viewMode: {
      default: false,
      type: Boolean,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      departmentCollapsed: false,
      dividerHide: false,
      enableSchedule: false,
      exclude: [],
      isAllDepartmentSelected: false,
      isOrderPeriodEndSameOrBeforeReceiveTimeStart: true,
      limitAmount: null,
      limitItems: null,
      listCollapsed: true,
      loading: false,
      menu: null,
      menuItems: [],
      openScheduleModal: false,
      openStoreSelectorModal: false,
      orderInfo: {},
      periodError: false,
      prepareUpdateSchedule: {},
      schedule: null,
      scheduleId: null,
      scheduleOrderList: [],
      settingCollapsed: false,
      storeCollapsed: false,
      pendingList: [],
    };
  },
  async created() {
    await this.getMenu();
    this.orderInfo = this.initializeOrderInfo();
    await this.loadDepartmentInfo();
  },
  watch: {
    departments() {
      this.departmentChanged();
    },
    submitAvailability(newVal) {
      this.$emit('valid', newVal);
    },
  },
  computed: {
    ...mapGetters('store', {
      paymentMethods: GET_PAYMENT_METHOD_MAPPING,
      paymentTimings: GET_PAYMENT_TIMING_MAPPING,
      orderMethods: GET_ORDER_METHOD_MAPPING,
    }),
    ...mapState(['isSidebarCollapsed']),
    ...mapState('orders', ['draftOrder', 'departments']),
    ...mapState('menu', ['rawMenu']),
    ...mapGetters('orders', [
      'currentDraftId',
      'draftMenuId',
      'draftStoreId',
      'draftBuyingMethod',
    ]),
    hasFirstGroup() {
      return !!this.groupBuyings?.length;
    },
    consumptionLimitAmount() {
      switch (this.orderInfo.limit) {
        case CONSUMPTION_LIMIT.AMOUNT:
          return this.limitAmount || 0;

        case CONSUMPTION_LIMIT.ITEMS:
          return this.limitItems || 0;

        default:
          return 0;
      }
    },
    consumptionLimitList() {
      return { ...CONSUMPTION_LIMIT };
    },
    hasEmailFills() {
      return this.v$?.orderInfo?.emailReceiver?.$dirty
        && this.v$?.orderInfo?.emailReceiver?.$errors.length;
    },
    inEndedOrOnGoing() {
      const methods = [
        ORDER_STATUS_WITH_TYPE_TEXT.ENDED,
        ORDER_STATUS_WITH_TYPE_TEXT.ONGOING,
      ];
      return methods.includes(this.draftBuyingMethod);
    },
    isAllowToEdit() {
      return this.allowEditType === ALLOW_EDIT_TYPES.ALL;
    },
    notifyDeadlineOptions() {
      return [
        {
          label: '30分鐘',
          value: 30,
        },
        {
          label: '1小時',
          value: 60,
        },
        {
          label: '2小時',
          value: 60 * 2,
        },
        {
          label: '4小時',
          value: 60 * 4,
        },
        {
          label: '8小時',
          value: 60 * 8,
        },
        {
          label: '1天',
          value: 60 * 24,
        },
        {
          label: '3天',
          value: 60 * 24 * 3,
        },
      ];
    },
    storePhone() {
      const orderMethod = this.menu?.storeInfo?.store_order_way || {};
      return orderMethod.phone || orderMethod.telephone || '';
    },
    storeClosedDays() {
      const closedDays = this.menu?.storeInfo?.store_close_days?.map((d) => d.day) || [];
      return weekdayTransformer(closedDays);
    },
    storePaymentMethods() {
      const storePaymentInfo = Object.keys(this.menu?.storeInfo?.store_payment_way || {})
        .filter((key) => this.menu.storeInfo?.store_payment_way[key]);
      const availablePaymentMethods = this.paymentMethods
        .filter((i) => storePaymentInfo.some((m) => i.key === m));
      return availablePaymentMethods.map((m) => m.title).join('、');
    },
    storeOrderMethods() {
      const excludedMethods = ['phone', 'telephone'];
      const orderMethod = this.menu?.storeInfo?.store_order_way || {};
      const availableOrderMethods = this.orderMethods
        .filter((i) => !excludedMethods.includes(i.key) && !!orderMethod[i.key]);
      return availableOrderMethods;
    },
    finalOrderInfo() {
      const orderInfo = _cloneDeep(this.orderInfo);
      const { id: menuId, storeInfo: { id: storeId } = {} } = this.menu || {};
      const groupBuyingSetting = {
        ...orderInfo,
        ...{
          limitAmount: this.consumptionLimitAmount,
          menuId,
          reportReceiver: orderInfo.emailReceiver,
          storeId,
          entreeLimits: this.menuItems
            .filter(({ limit }) => +limit >= 0 && limit !== '' && limit !== null)
            .map(({ index, limit }) => ({ entree_index: index, limit: +limit })),
        },
      };

      return {
        schedule_setting: {
          ...toSnakeCase(this.prepareUpdateSchedule),
          exclude: this.exclude,
        },
        group_buying_setting: toSnakeCase(groupBuyingSetting),
      };
    },
    minEntreeLimit() {
      return 0;
    },
    maxEntreeLimit() {
      return ENTREE_LIMIT.MAXIMUM;
    },
    submitAvailability() {
      const invalid = this.v$.$invalid;
      const { id: menuId, storeInfo: { id: storeId } = {} } = this.menu || {};
      return !invalid
        && !this.periodError
        && this.orderInfo?.departmentIds?.length
        && menuId
        && storeId
        && this.pendingList?.length;
    },
  },
  async mounted() {
    window.scrollTo({ top: 0 });
    window.addEventListener('resize', this.onWindowResize);
  },
  async unmounted() {
    window.removeEventListener('resize', this.onWindowResize);
  },
  methods: {
    ...mapActions('store', {
      getStoreList: GET_STORE_LIST,
      getStore: GET_STORE,
    }),
    ...mapActions('setting', {
      getDepartmentList: GET_DEPARTMENT_LIST,
    }),
    ...mapActions('store', {
      getMenuItem: GET_MENU_ITEM_LIST,
    }),
    ...mapActions('menu', {
      getGroupBuyingsMenu: GET_GROUP_BUYINGS_MENU,
    }),
    ...mapMutations('orders', {
      setDepartmentList: SET_DEPARTMENT_LIST,
      updateDraftOrder: UPDATE_DRAFT_ORDER,
    }),
    ...mapActions('orders', {
      createSchedule: CREATE_SCHEDULE,
      updateSchedule: UPDATE_SCHEDULE_VER2,
    }),
    beforeAddEmail(tag) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tag);
    },
    async getMenu() {
      const { storeId, menuId } = this.groupBuyingSetting;
      if (!storeId || !menuId) {
        return;
      }
      try {
        this.loading = true;
        const { menus, ...storeInfo } = await this.getStore(storeId);
        const menu = menus.find(({ id }) => id === menuId);
        if (menu) {
          this.menu = { ...menu, storeInfo };
        }
        await this.loadMenuItems(storeId, menuId);
      } catch (error) {
        const { message: msg = '' } = error;
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '取得菜單失敗，請重新選擇店家',
          msg,
        });
        this.menu = null;
      }
    },
    columnThAttrs() {
      return {
        class: 'text-xs',
      };
    },
    formatDate(date) {
      return dayjs(date).format('YYYY/MM/DD');
    },
    disabledTrash({ endTime: end = '', id, startTime: start = '' }) {
      const now = dayjs();
      return (id && this.inEndedOrOnGoing)
        || (!this.isAllowToEdit && (dayjs(end).isBefore(now) || dayjs(start).isBefore(now)));
    },
    disabledTime(time = '') {
      const now = dayjs();
      return !this.isAllowToEdit && dayjs(time).isBefore(now, 'm');
    },
    filterNonPositiveNumber($event) {
      const keyCode = $event.keyCode || $event.which;
      if (keyCode < 48 || keyCode > 57) {
        $event.preventDefault();
      }
    },
    initializeOrderInfo() {
      const { params: { scheduleId } = {} } = this.$route || {};
      const {
        departmentIds = [],
        emailReceiver = this.menu?.storeInfo?.store_detail?.emails?.slice() || [],
        enableOrderNote,
        enableSendEmail,
        enableSendLineNotifyInReceiveTime = false,
        limit = CONSUMPTION_LIMIT.NO_LIMIT,
        limitAmount = 0,
        name,
        note = '',
        notifyDeadline = false,
        notifyReserve = false,
        notifyStart = false,
        shiftMin = 30,
        subsidy = 0,
        tags = [],
      } = this.groupBuyingSetting;

      this.scheduleId = scheduleId;

      const orderInfo = {
        ...this.groupBuyingSetting,
        ...{
          departmentIds,
          emailReceiver,
          enableOrderNote: !!enableOrderNote,
          enableSendEmail: isBoolean(enableSendEmail) ? enableSendEmail : false,
          enableSendLineNotifyInReceiveTime,
          limit,
          limitAmount,
          name: this.createMode && name ? `${name}-1` : name,
          note,
          notifyDeadline,
          notifyReserve,
          notifyStart,
          shiftMin,
          subsidy,
          tags,
        },
      };

      if (isNil(orderInfo.limit)) {
        orderInfo.limit = CONSUMPTION_LIMIT.NO_LIMIT;
      }
      this.setConsumptionLimitAmount(orderInfo.limit, orderInfo.limitAmount);

      this.schedule = cloneDeep(this.scheduleSetting);
      this.exclude = this.scheduleSetting.exclude?.map((dateTime) => dayjs(dateTime).format('YYYY-MM-DD')) || [];
      if (this.enableSchedule) {
        this.v$.enableSchedule.$touch();
      }

      return orderInfo;
    },
    async setMenu(menu) {
      this.menu = menu;
      const { id: menuId, storeInfo: { id: storeId } } = this.menu;

      await this.loadMenuItems(storeId, menuId);
    },
    getMenuData(storeId, menuId) {
      const { params: { orderId } = {} } = this.$route || {};
      if (orderId && !this.reselectStore) {
        return this.getGroupBuyingsMenu(orderId);
      }
      return this.getMenuItem({ storeId, menuId });
    },
    async loadMenuItems(storeId, menuId) {
      try {
        this.loading = true;
        const { categories = [], entree = [] } = await this.getMenuData(storeId, menuId);
        const { entreeLimits = {} } = this.groupBuyingSetting || {};
        const limits = {};
        Object.values(entreeLimits).forEach(({ entreeIndex, limit }) => {
          limits[entreeIndex] = limit;
        });
        this.menuItems = _cloneDeep(entree)
          ?.filter(({ index }) => index !== ON_SITE_ORDER_ITEM_INDEX)
          ?.sort((a, b) => a.category_index - b.category_index
          || a.view_index - b.view_index)
          ?.map((item) => {
            const entreeLimit = limits[item.index] === 0
              ? 0
              : limits[item.index] || null;
            return {
              ...item,
              category: categories.find(({ index }) => index === item?.category_index)?.name || '',
              limit: isNil(entreeLimit) ? null : +(entreeLimit),
            };
          }) || [];
      } catch (ex) {
        Toast.open({
          type: 'is-danger',
          message: '載入菜單時發生錯誤，請稍後再試',
        });
      } finally {
        this.loading = false;
      }
    },
    getOrderMethodImage(filename) {
      const images = require.context('@/assets/images', false, /\.svg$/);
      return images(`./${filename}`);
    },
    datetimeFormatter(time) {
      return dayjs(time).format('YYYY/MM/DD a hh:mm');
    },
    isTimeBeforeNow(time) {
      const now = dayjs();
      return dayjs(time).isBefore(now);
    },
    prevStep() {
      this.$emit('prev-step');
    },
    setConsumptionLimitAmount(limit = this.orderInfo.limit, amount = 0) {
      switch (limit) {
        case CONSUMPTION_LIMIT.AMOUNT:
          this.limitAmount = amount;
          break;

        case CONSUMPTION_LIMIT.ITEMS:
          this.limitItems = amount;
          break;

        default:
          break;
      }
    },
    showLimitOption(val) {
      return !this.viewMode || (this.viewMode && this.orderInfo.limit === val);
    },
    focusOnLimitInput(target) {
      const limitInput = this.$refs[`limitInput${target}`];
      if (!limitInput) {
        return;
      }
      limitInput.focus();
    },
    onWindowResize() {
      const { width } = this.$refs.flexBox?.getBoundingClientRect() || {};
      if (!width) {
        return;
      }
      this.dividerHide = width < 390;
    },
    async loadDepartmentInfo() {
      try {
        this.loading = true;
        const departments = await this.getDepartmentList();
        this.setDepartmentList(departments);
      } catch {
        Toast.open({
          message: '載入部門資訊失敗，請重試一次',
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },
    departmentChanged() {
      const departmentsInOrder = this.orderInfo?.departmentIds || [];
      const departmentsOfCompany = this.departments?.map(({ id }) => id) || [];
      this.isAllDepartmentSelected = departmentsOfCompany.every(
        (id) => departmentsInOrder.includes(id),
      );
    },
    selectAllDepartments(selectAll) {
      this.orderInfo.departmentIds = selectAll ? this.departments?.map(({ id }) => id) : [];
    },
    onPeriodUpdate(data) {
      this.scheduleOrderList = V1Scheduler.getAllSchedule(data);
      this.prepareUpdateSchedule = {
        ...data,
        orderStart: data.startAtSameTime ? 0 : data.orderStart,
      };
      this.exclude = [];
    },
    onPeriodInit(data) {
      this.scheduleOrderList = V1Scheduler.getAllSchedule(data);
      this.prepareUpdateSchedule = {
        ...data,
        orderStart: data.startAtSameTime ? 0 : data.orderStart,
      };
    },
    enableSendEmailUpdate(val) {
      if (val && !this.orderInfo.emailReceiver?.length) {
        this.orderInfo.emailReceiver = this.menu?.storeInfo?.store_detail?.emails?.slice() || [];
      }
    },
    async onSubmit(id) {
      try {
        this.loading = true;
        if (this.createMode) {
          await this.createSchedule(this.finalOrderInfo);
          Toast.open({
            message: '已建立訂購排程',
            type: 'is-success',
          });
        } else {
          await this.updateSchedule({
            id: id || this.scheduleId,
            data: this.finalOrderInfo,
          });
          Toast.open({
            message: '已更新',
            type: 'is-success',
          });
        }

        if (!this.inDialog) {
          this.$router.push('/order/schedule');
        }
      } catch (error) {
        console.log(error);
        Toast.open({
          message: this.createMode ? '建立訂購排程失敗' : '更新失敗',
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.push('/order/schedule');
    },
  },
  validations() {
    return {
      limitAmount: {
        minValue: helpers.withMessage('輸入數字需要至少為1', minValue(1)),
        required: requiredIf('限制金額', this.orderInfo.limit === CONSUMPTION_LIMIT.AMOUNT),
      },
      limitItems: {
        minValue: helpers.withMessage('輸入數字需要至少為1', minValue(1)),
        required: requiredIf('限制品項數目', this.orderInfo.limit === CONSUMPTION_LIMIT.ITEMS),
      },
      orderInfo: {
        limit: {
          required: required('消費額度'),
        },
        name: {
          required: required('訂購名稱'),
        },
        emailReceiver: {
          required: requiredIf('Email', this.orderInfo.enableSendEmail),
        },
        subsidy: {
          minValue: helpers.withMessage('請輸入一個數字', minValue(0)),
          required: requiredIf('每人補助金額', this.orderInfo.subsidy !== 0),
        },
      },
      menuItems: {
        $each: helpers.forEach({
          limit: {
            integer: integer('訂購上限'),
            minValue: helpers.withMessage(`最少輸入：${this.minEntreeLimit}`, minValue(this.minEntreeLimit)),
            maxValue: helpers.withMessage(`最多輸入：${this.maxEntreeLimit}`, maxValue(this.maxEntreeLimit)),
          },
        }),
      },
    };
  },
};
